import { UserManagement } from '@/components/settings/UserManagement'
import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { User as ApiUser } from '@/types/api'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

const UsersPage: React.FC = () => {
  const { toast } = useToast()
  const [users, setUsers] = useState<ApiUser[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalUsers, setTotalUsers] = useState<number | undefined>(undefined)

  // Add filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const [showEdit, setShowEdit] = useState(false)
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editForm, setEditForm] = useState<{
    full_name: string
    email: string
    phone?: string
    role: 'customer' | 'admin'
  }>({ full_name: '', email: '', phone: '', role: 'customer' })

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null)

  const [showAdd, setShowAdd] = useState(false)
  const [isSubmittingAdd, setIsSubmittingAdd] = useState(false)
  const [addForm, setAddForm] = useState<{
    full_name: string
    email: string
    phone?: string
    role: 'customer' | 'admin'
    password: string
    password_confirmation: string
  }>({
    full_name: '',
    email: '',
    phone: '',
    role: 'customer',
    password: '',
    password_confirmation: '',
  })

  const loadUsers = useCallback(async () => {
    setIsLoading(true)
    try {
      // Build query parameters
      const params: Record<string, string | number | boolean> = {
        per_page: 10,
        page,
      }

      // Add search term if provided
      if (searchTerm) {
        params.search = searchTerm
      }

      // Add role filter if not "all"
      if (roleFilter !== 'all') {
        params.role = roleFilter
      }

      // Add status filter - map to backend parameters
      if (statusFilter === 'active') {
        params.email_verified = true
      } else if (statusFilter === 'inactive') {
        params.email_unverified = true
      }

      const res = await apiClient.getUsers(params)
      setUsers(res.data)
      const meta = res.meta
      if (meta && typeof meta.current_page === 'number' && typeof meta.last_page === 'number') {
        setTotalPages(meta.last_page)
      }
      if (meta && typeof meta.total === 'number') {
        setTotalUsers(meta.total)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Gagal memuat users'
      toast({ title: 'Error', description: message, variant: 'destructive' })
    } finally {
      setIsLoading(false)
    }
  }, [toast, page, searchTerm, roleFilter, statusFilter])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  const listUsers = useMemo<ListUser[]>(
    () =>
      users.map((u) => ({
        id: u.id,
        name: u.full_name || `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email,
        email: u.email,
        role: u.role,
        status: u.email_verified_at ? ('active' as const) : ('inactive' as const),
        createdAt: u.created_at,
        phone: u.phone,
      })),
    [users]
  )

  const handleAddUser = () => {
    setShowAdd(true)
    setAddForm({
      full_name: '',
      email: '',
      phone: '',
      role: 'customer',
      password: '',
      password_confirmation: '',
    })
  }

  const handleEditUser = (userId: string) => {
    const u = users.find((x) => x.id === userId)
    if (!u) return
    const name = u.full_name || `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email
    setEditForm({
      full_name: name,
      email: u.email,
      phone: u.phone,
      role: ['customer', 'admin'].includes(u.role) ? (u.role as 'customer' | 'admin') : 'admin',
    })
    setEditingUserId(userId)
    setShowEdit(true)
  }

  const handleDeleteUser = (userId: string) => {
    const u = users.find((x) => x.id === userId)
    const name = u
      ? u.full_name || `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim() || u.email
      : userId
    setUserToDelete({ id: userId, name })
    setShowDeleteDialog(true)
  }

  const handleResetPassword = async (userId: string) => {
    toast({ title: 'Reset Password', description: `Reset password untuk ${userId}` })
  }

  const handleToggleStatus = async (userId: string) => {
    toast({ title: 'Status', description: `Toggle status untuk ${userId}` })
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Users</h1>
        <p className="text-muted-foreground">Manage admin users and permissions</p>
      </div>

      <UserManagement
        users={listUsers}
        isLoading={isLoading}
        currentPage={page}
        totalPages={totalPages}
        totalUsers={totalUsers}
        onPageChange={setPage}
        onAddUser={handleAddUser}
        onEditUser={handleEditUser}
        onDeleteUser={handleDeleteUser}
        onResetPassword={handleResetPassword}
        onToggleStatus={handleToggleStatus}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        roleFilter={roleFilter}
        setRoleFilter={setRoleFilter}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
      />

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-md">
          <div className="space-y-4">
            <div>
              <label className="text-sm">Full Name</label>
              <Input
                value={editForm.full_name}
                onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm">Email</label>
              <Input
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm">Phone</label>
              <Input
                value={editForm.phone ?? ''}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm">Role</label>
              <select
                value={editForm.role}
                onChange={(e) =>
                  setEditForm({ ...editForm, role: e.target.value as 'customer' | 'admin' })
                }
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background w-full"
              >
                <option value="customer">Customer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowEdit(false)
                setEditingUserId(null)
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!editingUserId) return
                setIsSubmitting(true)
                try {
                  await apiClient.updateUser(editingUserId, {
                    full_name: editForm.full_name,
                    email: editForm.email,
                    phone: editForm.phone,
                    role: editForm.role,
                  })
                  toast({ title: 'Berhasil', description: 'User diperbarui' })
                  setShowEdit(false)
                  setEditingUserId(null)
                  setPage(1)
                  loadUsers()
                } catch (err) {
                  const message = err instanceof Error ? err.message : 'Gagal memperbarui user'
                  toast({ title: 'Error', description: message, variant: 'destructive' })
                } finally {
                  setIsSubmitting(false)
                }
              }}
              disabled={isSubmitting}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-md">
          <div className="space-y-4">
            <div>
              <label className="text-sm">Full Name</label>
              <Input
                value={addForm.full_name}
                onChange={(e) => setAddForm({ ...addForm, full_name: e.target.value })}
                placeholder="Enter full name"
              />
            </div>
            <div>
              <label className="text-sm">Email</label>
              <Input
                type="email"
                value={addForm.email}
                onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                placeholder="Enter email address"
              />
            </div>
            <div>
              <label className="text-sm">Phone</label>
              <Input
                value={addForm.phone ?? ''}
                onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                placeholder="Enter phone number (optional)"
              />
            </div>
            <div>
              <label className="text-sm">Role</label>
              <select
                value={addForm.role}
                onChange={(e) =>
                  setAddForm({ ...addForm, role: e.target.value as 'customer' | 'admin' })
                }
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background w-full"
              >
                <option value="customer">Customer</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="text-sm">Password</label>
              <Input
                type="password"
                value={addForm.password}
                onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                placeholder="Enter password"
              />
            </div>
            <div>
              <label className="text-sm">Confirm Password</label>
              <Input
                type="password"
                value={addForm.password_confirmation}
                onChange={(e) => setAddForm({ ...addForm, password_confirmation: e.target.value })}
                placeholder="Confirm password"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowAdd(false)
                setAddForm({
                  full_name: '',
                  email: '',
                  phone: '',
                  role: 'customer',
                  password: '',
                  password_confirmation: '',
                })
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={async () => {
                if (!addForm.full_name || !addForm.email || !addForm.password) {
                  toast({
                    title: 'Error',
                    description: 'Please fill in all required fields',
                    variant: 'destructive',
                  })
                  return
                }
                if (addForm.password !== addForm.password_confirmation) {
                  toast({
                    title: 'Error',
                    description: 'Passwords do not match',
                    variant: 'destructive',
                  })
                  return
                }
                setIsSubmittingAdd(true)
                try {
                  await apiClient.createUser({
                    full_name: addForm.full_name,
                    email: addForm.email,
                    phone: addForm.phone,
                    role: addForm.role,
                    password: addForm.password,
                    password_confirmation: addForm.password_confirmation,
                  })
                  toast({ title: 'Berhasil', description: 'User berhasil ditambahkan' })
                  setShowAdd(false)
                  setAddForm({
                    full_name: '',
                    email: '',
                    phone: '',
                    role: 'customer',
                    password: '',
                    password_confirmation: '',
                  })
                  setPage(1)
                  loadUsers()
                } catch (err) {
                  const message = err instanceof Error ? err.message : 'Gagal menambahkan user'
                  toast({ title: 'Error', description: message, variant: 'destructive' })
                } finally {
                  setIsSubmittingAdd(false)
                }
              }}
              disabled={isSubmittingAdd}
            >
              Add User
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        _open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete User"
        description="Yakin ingin menghapus user ini? Tindakan ini tidak dapat dibatalkan."
        details={userToDelete ? userToDelete.name : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={async () => {
          if (!userToDelete) return
          try {
            await apiClient.deleteUser(userToDelete.id)
            toast({ title: 'Berhasil', description: 'User dihapus' })
            setShowDeleteDialog(false)
            setUserToDelete(null)
            setPage(1)
            loadUsers()
          } catch (err) {
            const message = err instanceof Error ? err.message : 'Gagal menghapus user'
            toast({ title: 'Error', description: message, variant: 'destructive' })
          }
        }}
        variant="destructive"
      />
    </div>
  )
}

export default UsersPage
type ListUser = {
  id: string
  name: string
  email: string
  role: string
  status?: 'active' | 'inactive'
  createdAt?: string
  phone?: string
}
